import IconPlus from "@common/components/icons/IconPlus";
import ErrorMessage from "@common/components/text/ErrorMessage";
import {
  Box,
  Button,
  Card,
  Flex,
  getDefaultZIndex,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import { purchasesStore } from "@stores/purchases";
import { ReactElement } from "react";
import { NewInstanceDropdownLink } from "./NewInstanceDropdown.components";
import { useStyles } from "./NewInstanceDropdownLink.styled";
import { canAccountCreateNewProductionInstance } from "@stores/purchases/utils";
import IconSQL from "@common/components/icons/IconSQL";
import IconHardDrive from "@common/components/icons/IconHardDrive";

export default function NewInstanceDropdown(): ReactElement {
  const theme = useMantineTheme();
  const { classes } = useStyles({});

  const {
    isLoading: loadingPurchases,
    error: purchasesError,
    data: purchases,
  } = purchasesStore.useGetPurchases();

  // new instances?
  const canCreateNewProductionInstance =
    (purchases && canAccountCreateNewProductionInstance(purchases)) || false;

  // toggle dropdown
  const [opened, { close: closeDropdown, toggle: toggleDropdown }] =
    useDisclosure(false);

  const ref = useClickOutside(closeDropdown);

  return (
    <>
      {purchasesError && (
        <Flex
          align="center"
          direction="row"
          justify="center"
          h={2 * theme.spacing.lg}
          px={(theme.spacing.sm + theme.spacing.md) / 2}
          py="xs"
        >
          <ErrorMessage error={purchasesError} />
        </Flex>
      )}

      <Box ref={ref} pos="relative">
        <Button
          data-testid="create-new-instance-link"
          variant="outline"
          color="white"
          fw={700}
          fz="sm"
          loading={loadingPurchases}
          disabled={loadingPurchases}
          leftIcon={loadingPurchases ? <Loader size="xs" /> : <IconPlus />}
          onClick={toggleDropdown}
        >
          New instance
        </Button>

        {!loadingPurchases && opened && (
          <Box
            pos="absolute"
            pt="xs"
            right={-theme.spacing.xs}
            top="100%"
            // mantine's card (`position: relative`) < popover zIndex (300) < disabled copy tooltip's zIndex (300)
            style={{ zIndex: getDefaultZIndex("popover") }}
          >
            <Card className={classes.dropdownCard} withBorder miw={250} p="xs">
              <NewInstanceDropdownLink
                data-testid="create-new-prod-instance-link"
                disabled={!canCreateNewProductionInstance}
                disabledCopy="You're already running all your available trial instances"
                href="/account/new-instance"
                copy="Production instance"
                icon={<IconHardDrive />}
              />

              <NewInstanceDropdownLink
                data-testid="create-new-dev-instance-link"
                disabledCopy="You need a active pro subscription"
                href="/account/new-dev-instance"
                copy="Development instance"
                icon={<IconSQL />}
                highlight
              />
            </Card>
          </Box>
        )}
      </Box>
    </>
  );
}
